import { startOfWeek, addWeeks, isAfter, format, subDays } from 'date-fns'
import type { Bucket, Priority } from '@/types'

// ─── Output types ─────────────────────────────────────────────────────────────

export interface BucketAlloc {
  bucketId: string
  weight: number
  share: number        // 0–1 fraction of total window weight
  targetAlloc: number | null
  gap: number | null   // targetAlloc - share; positive = under-invested
}

export interface WeekSlice {
  weekStart: string    // 'yyyy-MM-dd' (Monday)
  slices: Array<{
    bucketId: string
    color: string
    share: number      // fraction within this week
    weight: number
  }>
  totalWeight: number  // 0 if no cadence data this week
}

export interface AllocationResult {
  byBucket: BucketAlloc[]
  byPriority: Record<string, number>  // priorityId → share of total
  weekSlices: WeekSlice[]
  totalWeight: number
  alignmentScore: number | null       // null when < 2 buckets have both target + data
  committedHoursPerWeek: number       // steady-state estimate, not time-bound
  bandwidthStatus: 'ok' | 'amber' | 'red'
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** CL-4: hours per week estimate. load_weight × 10 when hoursPerWeek not set. */
export function priorityHoursPerWeek(p: Priority): number {
  return p.hoursPerWeek ?? (p.loadWeight != null ? p.loadWeight * 10 : 0)
}

function isFirstWeekOfMonth(weekStart: Date): boolean {
  return subDays(weekStart, 7).getMonth() !== weekStart.getMonth()
}

function isFirstWeekOfQuarter(weekStart: Date): boolean {
  return [0, 3, 6, 9].includes(weekStart.getMonth()) && isFirstWeekOfMonth(weekStart)
}

/** CL-6: does this priority's cadence apply to a given week? */
export function cadenceActiveInWeek(p: Priority, weekStart: Date, weekIdx: number): boolean {
  switch (p.cadence) {
    case 'daily':
    case 'weekly':
      return true
    case 'biweekly':
      return weekIdx % 2 === 0
    case 'monthly':
      return isFirstWeekOfMonth(weekStart)
    case 'quarterly':
      return isFirstWeekOfQuarter(weekStart)
    default:
      return false  // null cadence → no contribution (CL-6)
  }
}

/** All Monday-starting weeks that overlap [from, to]. */
export function getWeekStarts(from: Date, to: Date): Date[] {
  const starts: Date[] = []
  let current = startOfWeek(from, { weekStartsOn: 1 })
  while (!isAfter(current, to)) {
    starts.push(current)
    current = addWeeks(current, 1)
  }
  return starts
}

// ─── Core computation ─────────────────────────────────────────────────────────

/**
 * Compute allocation from cadence inference (Phase 2).
 * Time logs and check-ins replace cadence inference in Phase 3.
 * Implements CL-1, CL-2, CL-4, CL-5, CL-6.
 */
export function computeAllocation(
  priorities: Priority[],
  buckets: Bucket[],
  from: Date,
  to: Date,
  amberHours = 55,
  redHours = 65,
  skips: Set<string> = new Set(), // 'priorityId:weekStart' strings
): AllocationResult {
  const active = priorities.filter(
    (p) => !p.archivedAt && p.status !== 'released' && p.status !== 'completed',
  )
  const bucketMap = new Map(buckets.map((b) => [b.id, b]))
  const systemBucketId = buckets.find((b) => b.isSystem && !b.archivedAt)?.id ?? '__system'
  const weekStarts = getWeekStarts(from, to)

  // Steady-state committed hours/week — not time-window-bound (for bandwidth bar)
  const committedHoursPerWeek = active.reduce((sum, p) => {
    const h = priorityHoursPerWeek(p)
    if (!h || !p.cadence) return sum
    const factor =
      p.cadence === 'daily' || p.cadence === 'weekly' ? 1
      : p.cadence === 'biweekly' ? 0.5
      : p.cadence === 'monthly' ? 1 / 4.33
      : p.cadence === 'quarterly' ? 1 / 13
      : 0
    return sum + h * factor
  }, 0)

  // Single pass: build per-week bucket maps and per-priority totals
  const weekMaps: Map<string, number>[] = weekStarts.map(() => new Map())
  const priorityTotals = new Map<string, number>()
  let totalWeight = 0

  for (const p of active) {
    const h = priorityHoursPerWeek(p)
    if (!h || !p.cadence) continue
    const bid = p.bucketId ?? systemBucketId
    weekStarts.forEach((ws, idx) => {
      if (!cadenceActiveInWeek(p, ws, idx)) return
      if (skips.has(`${p.id}:${format(ws, 'yyyy-MM-dd')}`)) return
      weekMaps[idx]!.set(bid, (weekMaps[idx]!.get(bid) ?? 0) + h)
      priorityTotals.set(p.id, (priorityTotals.get(p.id) ?? 0) + h)
      totalWeight += h
    })
  }

  // weekSlices for calendar rendering
  const weekSlices: WeekSlice[] = weekStarts.map((ws, idx) => {
    const bw = weekMaps[idx]!
    const weekTotal = Array.from(bw.values()).reduce((s, v) => s + v, 0)
    const slices = Array.from(bw.entries())
      .map(([bucketId, weight]) => ({
        bucketId,
        color: bucketMap.get(bucketId)?.color ?? '#888888',
        share: weekTotal > 0 ? weight / weekTotal : 0,
        weight,
      }))
      .sort((a, b) => b.weight - a.weight)
    return { weekStart: format(ws, 'yyyy-MM-dd'), slices, totalWeight: weekTotal }
  })

  // Per-bucket totals (sum across all weeks)
  const bucketTotals = new Map<string, number>()
  for (const wm of weekMaps) {
    for (const [bid, w] of wm) {
      bucketTotals.set(bid, (bucketTotals.get(bid) ?? 0) + w)
    }
  }

  // byBucket — one entry per active bucket
  const byBucket: BucketAlloc[] = buckets
    .filter((b) => !b.archivedAt)
    .map((b) => {
      const weight = bucketTotals.get(b.id) ?? 0
      const share = totalWeight > 0 ? weight / totalWeight : 0
      const targetAlloc = b.targetAlloc ?? null
      const gap = targetAlloc != null ? targetAlloc - share : null
      return { bucketId: b.id, weight, share, targetAlloc, gap }
    })

  // byPriority shares
  const byPriority: Record<string, number> = {}
  for (const [id, w] of priorityTotals) {
    byPriority[id] = totalWeight > 0 ? w / totalWeight : 0
  }

  // CL-5: alignment score
  // score = 1 - Σ(bucket_weight × |gap|) / Σ(bucket_weight)
  // bucket_weight = 1 / bucket.rank
  const scorable = byBucket.filter((b) => b.gap !== null)
  let alignmentScore: number | null = null
  if (scorable.length >= 2) {
    let num = 0
    let den = 0
    for (const b of scorable) {
      const bucket = bucketMap.get(b.bucketId)
      if (!bucket) continue
      const bw = 1 / Math.max(bucket.rank, 0.01)
      num += bw * Math.abs(b.gap!)
      den += bw
    }
    alignmentScore = den > 0
      ? Math.max(0, Math.min(100, Math.round((1 - num / den) * 100)))
      : null
  }

  const bandwidthStatus: AllocationResult['bandwidthStatus'] =
    committedHoursPerWeek >= redHours ? 'red'
    : committedHoursPerWeek >= amberHours ? 'amber'
    : 'ok'

  return {
    byBucket,
    byPriority,
    weekSlices,
    totalWeight,
    alignmentScore,
    committedHoursPerWeek,
    bandwidthStatus,
  }
}
